<template>
  <div class="assignee-selector">
    <div
      class="assignee-display"
      @click="toggleDropdown"
    >
      <div
        v-if="selectedMembers.length === 0"
        class="no-assignee"
      >
        <div>
          <feather-icon
            icon="UserIcon"
            size="14"
            class="mr-50"
          />
          <span class="text-muted">{{ $t('Select Users') }}</span>
        </div>
      </div>
      <div
        v-else
        class="assignee-list"
      >
        <div
          v-for="member in selectedMembers.slice(0, 3)"
          :key="member?.uuid || member"
          class="assignee-item"
        >
          <b-avatar
            :src="getStorage(member.avatar)"
            :text="getInitials(member.name || member.email || 'Unassigned')"
            size="24"
            variant="light-primary"
          >
            <feather-icon
              v-if="member.icon"
              :icon="member.icon"
              class="text-secondary"
              size="14"
            />
          </b-avatar>
          <span class="font-weight-bold">{{ member.name || member.email }}</span>
          <div
            v-if="clearable"
            @click.stop="toggleMember(member)"
          >
            <feather-icon
              icon="XIcon"
              size="12"
              class="remove-icon ml-25"
            />
          </div>
        </div>
        <span
          v-if="selectedMembers.length > 3"
          class="more-count"
        >
          +{{ selectedMembers.length - 3 }} more
        </span>
      </div>
      <feather-icon
        icon="ChevronDownIcon"
        size="16"
        class="ml-auto"
        :class="{ 'rotate-180': showDropdown }"
      />
    </div>

    <transition name="dropdown-fade">
      <div
        v-if="showDropdown"
        class="assignee-dropdown"
        @click.stop
      >
        <div class="dropdown-header">
          <h6 class="mb-0">
            {{ $t('Select assignees') }}
          </h6>
          <b-button
            variant="link"
            size="sm"
            class="p-0"
            @click="toggleDropdown"
          >
            <feather-icon
              icon="XIcon"
              size="18"
            />
          </b-button>
        </div>

        <div class="dropdown-search">
          <div class="search-input-wrapper">
            <feather-icon
              icon="SearchIcon"
              size="14"
              class="search-icon"
            />
            <b-form-input
              v-model="searchQuery"
              :placeholder="$t('Filter assignees')"
              size="sm"
              class="search-input"
            />
          </div>
        </div>

        <div class="dropdown-content">
          <!-- Group Assignees -->
          <div
            v-if="filteredMembers.length > 0"
            class="assignee-section"
          >
            <div class="section-label">
              {{ $t('Group assignees') }}
            </div>
            <div
              v-for="member in filteredMembers"
              :key="member.uuid"
              class="member-item"
              @click="toggleMember(member)"
            >
              <div class="member-info">
                <b-avatar
                  :src="member.avatar"
                  :icon="member.icon"
                  :text="getInitials(member.name || member.email)"
                  size="20"
                  variant="light-primary"
                  class="mr-50"
                >
                  <feather-icon
                    v-if="member.icon"
                    :icon="member.icon"
                    class="text-secondary"
                    size="14"
                  />
                </b-avatar>
                <div class="member-details">
                  <div class="member-name">
                    {{ member.name || member.email }}
                  </div>
                  <div
                    v-if="member.role"
                    class="member-role"
                  >
                    {{ member.role }}
                  </div>
                </div>
                <feather-icon
                  v-if="isMemberSelected(member.uuid)"
                  icon="CheckIcon"
                  size="16"
                  class="text-success ml-auto"
                />
              </div>
            </div>
          </div>

          <!-- No Results -->
          <div
            v-if="filteredMembers.length === 0"
            class="no-results"
          >
            <feather-icon
              icon="UserXIcon"
              size="32"
              class="text-muted mb-1"
            />
            <p class="text-muted mb-0">
              {{ $t('No members found') }}
            </p>
          </div>

          <!-- Suggestions Section (optional) -->
          <div
            v-if="suggestedMembers.length > 0 && !searchQuery"
            class="assignee-section"
          >
            <div class="section-label">
              {{ $t('Suggestions') }}
            </div>
            <div
              v-for="member in suggestedMembers"
              :key="'suggested-' + member.uuid"
              class="member-item suggestion"
              @click="toggleMember(member)"
            >
              <div class="member-info">
                <b-avatar
                  :src="getStorage(member.avatar)"
                  :text="getInitials(member.name || member.email)"
                  size="20"
                  variant="light-primary"
                  class="mr-50"
                />
                <div class="member-details">
                  <div class="member-name">
                    {{ member.name || member.email }}
                  </div>
                  <b-badge
                    v-if="member.isCopilot"
                    variant="light-secondary"
                    class="ml-50"
                  >
                    <feather-icon
                      icon="CpuIcon"
                      size="10"
                      class="mr-25"
                    />
                    AI
                  </b-badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Overlay -->
    <div
      v-if="showDropdown"
      class="assignee-overlay"
      @click="toggleDropdown"
    />
  </div>
</template>

<script>
import {
  BAvatar,
  BButton,
  BFormInput,
  BBadge,
} from 'bootstrap-vue'

export default {
  name: 'AssigneeSelector',
  components: {
    BAvatar,
    BButton,
    BFormInput,
    BBadge,
  },
  props: {
    value: {
      type: [String, Array],
      default: null,
    },
    members: {
      type: Array,
      default: () => [],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showDropdown: false,
      searchQuery: '',
      selectedUuids: [],
    }
  },
  computed: {
    selectedMembers() {
      return this.members.filter(m => this.selectedUuids.includes(m.uuid))
    },
    filteredMembers() {
      if (!this.searchQuery) {
        return this.members
      }
      const query = this.searchQuery.toLowerCase()
      return this.members.filter(m => {
        const name = (m.name || m.email || '').toLowerCase()
        const email = (m.email || '').toLowerCase()
        return name.includes(query) || email.includes(query)
      })
    },
    suggestedMembers() {
      // Could be based on recent assignments, project managers, etc.
      return []
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (this.multiple) {
          // eslint-disable-next-line no-nested-ternary
          this.selectedUuids = Array.isArray(val) ? val : (val ? [val] : [])
        } else {
          this.selectedUuids = val ? [val] : []
        }
      },
    },
  },
  methods: {
    toggleDropdown() {
      this.showDropdown = !this.showDropdown
      if (this.showDropdown) {
        this.$nextTick(() => {
          const input = this.$el.querySelector('.search-input')
          if (input) input.focus()
        })
      }
    },
    toggleMember(member) {
      if (member.uuid === 'unassigned') {
        if (this.selectedUuids.includes('unassigned')) {
          this.selectedUuids = []
          this.$emit('input', this.multiple ? [] : null)
        } else {
          this.selectedUuids = ['unassigned']
          this.$emit('input', ['unassigned'])
        }
        return
      }

      this.selectedUuids = this.selectedUuids.filter(uuid => uuid !== 'unassigned')
      if (this.multiple) {
        const index = this.selectedUuids.indexOf(member.uuid)
        if (index > -1) {
          this.selectedUuids.splice(index, 1)
        } else {
          this.selectedUuids.push(member.uuid)
        }
        this.$emit('input', this.selectedUuids)
      } else {
        this.selectedUuids = [member.uuid]
        this.$emit('input', member.uuid)
        this.showDropdown = false
      }
    },
    isMemberSelected(uuid) {
      return this.selectedUuids.includes(uuid)
    },
    getInitials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/base/pages/kanban-side-panel.scss";
</style>
